import { Component, Inject, ViewChild, AfterViewInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { DirectorOpbCompanyConnectionDto, DirectorCompanyMembershipDto } from '../../../../../../models/api/companyBoardMemberDto';
import { OpbCompanyDto, OpbCompaniesSearchRequest } from '../../../../../../models/api/opbCompanyDto';
import { OpbCompanyService } from '../../../../../../services/opb-company.service';
import { CompanyBoardService } from '../../../../../../services/companies/company-board.service';
import { Helpers } from '../../../../../../utilities/helpers';

export interface ConnectionsDialogData {
  memberName: string;
  memberId: string;
  companyId: string;
  year: number;
  isExecutive: boolean;
  opbCompanyConnections: DirectorOpbCompanyConnectionDto[];
  otherCompanyMemberships: DirectorCompanyMembershipDto[];
}

interface ConnectionRow {
  name: string;
  type: 'opb' | 'other';
  opbCompanyId?: string;
  companyId?: string;
  membershipLabel?: string;
}

@Component({
  selector: 'app-connections-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    MatFormFieldModule,
    MatTooltipModule,
    FormsModule,
    NgxSpinnerModule
  ],
  templateUrl: './connections-dialog.component.html',
  styleUrls: ['./connections-dialog.component.scss']
})
export class ConnectionsDialogComponent implements AfterViewInit {
  protected readonly Helpers = Helpers;
  private opbCompanyService = inject(OpbCompanyService);
  private boardService = inject(CompanyBoardService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private router = inject(Router);

  // Connections table
  connectionsDataSource = new MatTableDataSource<ConnectionRow>([]);
  connectionsColumns: string[] = ['name', 'type', 'actions'];

  // Search table
  searchDataSource = new MatTableDataSource<OpbCompanyDto>([]);
  searchColumns: string[] = ['name', 'countryName', 'sectorName', 'actions'];
  searchName = '';
  pageSize = 5;

  changed = false;

  @ViewChild('searchSort') searchSort!: MatSort;
  @ViewChild('searchPaginator') searchPaginator!: MatPaginator;

  constructor(
    public dialogRef: MatDialogRef<ConnectionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConnectionsDialogData
  ) {
    this.buildConnectionsTable();
  }

  ngAfterViewInit(): void {
    this.searchDataSource.sort = this.searchSort;
    this.searchDataSource.paginator = this.searchPaginator;
  }

  onSearch(): void {
    if (!this.searchName?.trim()) return;

    const request: OpbCompaniesSearchRequest = {
      name: this.searchName,
      pageNumber: 1,
      pageSize: 100,
      sortBy: 'Name',
      ascending: true
    };

    this.spinnerService.show();
    this.opbCompanyService.searchOpbCompanies(request).subscribe({
      next: (response) => {
        this.searchDataSource.data = response.data;
        if (this.searchPaginator) {
          this.searchPaginator.firstPage();
        }
        this.spinnerService.hide();
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Error searching OPB companies');
      }
    });
  }

  onDeleteOpbConnection(row: ConnectionRow): void {
    if (!row.opbCompanyId) return;

    this.spinnerService.show();
    const delete$ = this.data.isExecutive
      ? this.boardService.deleteExecutiveOpbConnection(this.data.companyId, this.data.memberId, row.opbCompanyId, this.data.year)
      : this.boardService.deleteOpbConnection(this.data.companyId, this.data.memberId, row.opbCompanyId, this.data.year);

    delete$.subscribe({
      next: () => {
        this.data.opbCompanyConnections = this.data.opbCompanyConnections.filter(c => c.opbCompanyId !== row.opbCompanyId);
        this.buildConnectionsTable();
        this.changed = true;
        this.spinnerService.hide();
        this.toast.success('Connection removed');
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Failed to remove connection');
      }
    });
  }

  onAddOpbConnection(opbCompany: OpbCompanyDto): void {
    if (this.isAlreadyConnected(opbCompany.id)) {
      this.toast.info('This company is already connected');
      return;
    }

    this.spinnerService.show();
    const add$ = this.data.isExecutive
      ? this.boardService.addExecutiveOpbConnection(this.data.companyId, this.data.memberId, opbCompany.id, this.data.year)
      : this.boardService.addOpbConnection(this.data.companyId, this.data.memberId, opbCompany.id, this.data.year);

    add$.subscribe({
      next: () => {
        this.data.opbCompanyConnections.push({
          opbCompanyId: opbCompany.id,
          opbCompanyName: opbCompany.name
        });
        this.buildConnectionsTable();
        this.changed = true;
        this.spinnerService.hide();
        this.toast.success('Connection added');
      },
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Failed to add connection');
      }
    });
  }

  isAlreadyConnected(opbCompanyId: string): boolean {
    return this.data.opbCompanyConnections.some(c => c.opbCompanyId === opbCompanyId);
  }

  getCompanyUrl(row: ConnectionRow): string {
    if (row.type === 'opb') {
      return this.router.serializeUrl(this.router.createUrlTree(['/companies/opb-edit', row.opbCompanyId]));
    }
    return this.router.serializeUrl(this.router.createUrlTree(['/companies/edit', row.companyId], { queryParams: { tab: 'board' } }));
  }

  onClose(): void {
    this.dialogRef.close(this.changed);
  }

  private getMembershipLabel(isExecutive: boolean, boardType?: number): string {
    if (isExecutive) return 'Executive Management';
    switch (boardType) {
      case 1: return 'Supervisory Board';
      case 2: return 'Management Board';
      default: return 'Board of Directors';
    }
  }

  private buildConnectionsTable(): void {
    const opbRows: ConnectionRow[] = this.data.opbCompanyConnections
      .map(c => ({ name: c.opbCompanyName, type: 'opb' as const, opbCompanyId: c.opbCompanyId }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const otherRows: ConnectionRow[] = this.data.otherCompanyMemberships
      .map(c => ({
        name: c.companyName,
        type: 'other' as const,
        companyId: c.companyId,
        membershipLabel: this.getMembershipLabel(c.isExecutive, c.boardType)
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    this.connectionsDataSource.data = [...opbRows, ...otherRows];
  }
}
